import Swiper from 'swiper';
import {
    Autoplay,
    Navigation,
    Pagination,
    // EffectFade,
    // EffectCoverflow,
    // Parallax,
} from 'swiper/modules';

import type { SectionAjaxData } from './AjaxForm';
import { AjaxForm } from './AjaxForm';
import { choices, swiperColumns } from './helpers';

interface ExtendedElement extends HTMLElement {
    trigger: ExtendedElement;
    target: ExtendedElement;
    offClickHandler: (event: Event) => void;
}

type AppAnimation = {
    from: Keyframe;
    to: Keyframe;
    showTimeout: number;
    hideTimeout: number;
    hoverTimeout: number;
};

export default class App {
    animation: AppAnimation = {
        from: { opacity: 0, transform: 'scale(0.95)' },
        to: { opacity: 1, transform: 'scale(1)' },
        showTimeout: 100,
        hideTimeout: 75,
        hoverTimeout: 300,
    };

    constructor() {
        this.navObserver();
        this.sectionObserver();

        this.setupCollapsibles();
        this.setupDropdowns();
        this.navDropdown();

        this.swiper();

        const archive = document.querySelector('.section__archive') as HTMLElement;
        if (archive) {
          new AjaxForm(archive);
        }

        document.querySelectorAll('.js-choices').forEach((element) => {
            choices(element as HTMLSelectElement);
        });
    }

    swiper() {
        const containers = document.querySelectorAll<HTMLElement>('.swiper-container');
        containers?.forEach((container) => {
            const swiperElement = container.querySelector('.swiper') as HTMLElement;
            const columns = +(container.dataset.columns ?? 1);
            const autoplay = container.dataset.autoplay === '1';
            const reverse = container.dataset.reverse === '1';
            const breakpoints = swiperColumns(columns);

            const swiper = new Swiper(swiperElement, {
                // effect: 'coverflow',
                // grabCursor: true,
                // centeredSlides: true,
                slidesPerView: 1,
                loop: true,
                spaceBetween: 0,
                autoplay: autoplay && {
                    delay: 3000,
                    reverseDirection: reverse,
                },
                breakpoints,
                keyboard: {
                    enabled: true,
                },
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                    enabled: true,
                },
                navigation: {
                    nextEl: container.querySelector('.swiper-button-next') as HTMLElement,
                    prevEl: container.querySelector('.swiper-button-prev') as HTMLElement,
                },
                // coverflowEffect: {
                //   rotate: -20,
                //   stretch: 0,
                //   depth: 100,
                //   modifier: 1,
                //   slideShadows: false,
                // },
                modules: [Navigation, Pagination, Autoplay], //EffectCoverflow
            });
        });
    }

    sectionObserver() {
        const sections: NodeListOf<HTMLElement> = document.querySelectorAll('.bg_type-block, .bg_type-line, .section__intro, .section__sliding_door, .section__testimonials');
        // on small screens dont animate 'in-view'
        if (window.matchMedia('(max-width: 767px)').matches) {
            sections.forEach((section) => {
                section.classList.add('in-view');
            });
            return;
        }


        const observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                    } else {
                        entry.target.classList.remove('in-view');
                    }
                });
            },
            {
                root: null, // relative to document viewport
                rootMargin: '0px',
                threshold: 0.1,
            }
        );

        function updateParallax() {
            const scrollTop = document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;

            sections.forEach((section) => {
                if (!section.classList.contains('in-view')) return;

                const rect = section.getBoundingClientRect();
                const sectionTop = rect.top + scrollTop;
                const sectionHeight = section.offsetHeight;

                // Calculate parallax offset based on scroll position relative to section
                const relativePos = (scrollTop - sectionTop + windowHeight) / (sectionHeight + windowHeight);
                const parallaxOffset = (section.classList.contains('reverse') ? (0.5 - relativePos) : (relativePos - 0.5));

                const svg: SVGElement|null = section.querySelector('.section-bg');
                if (svg) {
                    svg.style.transform = `translate3d(0, ${parallaxOffset  * 200}px, 0)`;
                }
            });
        }

        sections.forEach((section) => {
            const offset = (Math.random() -.5) * 100;
            const svg: SVGElement|null = section.querySelector('.section-bg');
            if(svg){
                svg.style.top =  offset + '%';
            }

            observer.observe(section);
        });

        window.addEventListener('scroll', updateParallax, { passive: true });
    }

    navObserver() {
        const header = document.querySelector('.header');
        const top = document.querySelector('#to-content');

        if (!header) {
            console.warn('Missing header!');
            return;
        }
        if (!top) {
            console.warn('Missing skip link!');
            return;
        }

        const topObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    header.classList.toggle('is-sticky', !entry.isIntersecting);
                });
            },
            {
                rootMargin: header.classList.contains('home')
                    ? '0px 0px 0px 0px'
                    : '-200px 0px 0px 0px',
                threshold: 1.0,
            }
        );

        if (window.matchMedia('(max-width: 767px)').matches) {
            header.classList.add('is-sticky');
        }

        if (!header.classList.contains('is-sticky')) {
            topObserver.observe(top);
        }

        window.addEventListener(
            'resize',
            () => {
                if (window.matchMedia('(max-width: 767px)').matches) {
                    header.classList.add('is-sticky');
                    topObserver.unobserve(top);
                }
            },
            false
        );
    }

    navDropdown() {
        const collapsible = document.querySelectorAll(
            '.navigation-toggle[data-collapse-toggle]'
        );
        collapsible?.forEach((collapseTrigger) => {
            const controlsId = collapseTrigger.getAttribute('data-collapse-controls');
            if (!controlsId) {
                console.warn('Missing controls id for navigation dropdown!');
                return;
            }
            const controlsElement = document.querySelector(controlsId);
            if (!controlsElement) {
                console.warn('Missing controls element for navigation dropdown!');
                return;
            }
            const targetId = collapseTrigger.getAttribute('data-collapse-toggle');
            if (!targetId) {
                console.warn('Missing target id for navigation dropdown!');
                return;
            }
            const targetElement = document.querySelector(`${targetId}`);
            if (!targetElement) {
                console.warn('Missing target for navigation dropdown!');
                return;
            }

            let timer: ReturnType<typeof setTimeout>;
            const closeDropdown = () => {
                clearTimeout(timer);
                collapseTrigger.setAttribute('aria-expanded', 'false');
                targetElement.classList.remove('show-navigation');

                // wait for animation to finish
                const items = controlsElement!.querySelectorAll('.navigation-item');
                const count = items.length;
                timer = setTimeout(
                    () => {
                        // display: none; - kills the animation
                        controlsElement?.classList.add('hidden');
                    },
                    500 + count * 100 // 500ms for nav to fade in and then no of items * 100ms
                );
            };
            const openDropdown = () => {
                clearTimeout(timer);
                collapseTrigger.setAttribute('aria-expanded', 'true');
                // remove display: none; - so we can animate
                controlsElement?.classList.remove('hidden');

                // actually wait for another frame
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        targetElement.classList.add('show-navigation');
                    });
                });
            };

            collapseTrigger.addEventListener('click', () => {
                collapseTrigger.getAttribute('aria-expanded') === 'true'
                    ? closeDropdown()
                    : openDropdown();
            });
            document.addEventListener('keydown', function (e) {
                if (
                    e.key === 'Escape' &&
                    targetElement.classList.contains('show-navigation')
                ) {
                    closeDropdown();
                }
            });
        });
    }

    setupCollapsibles() {
        // let timer: number | undefined;
        const collapsible = document.querySelectorAll(
            ':not(.navigation-toggle)[data-collapse-toggle]'
        );
        collapsible?.forEach((collapseTrigger) => {
            const targetId = collapseTrigger.getAttribute('data-collapse-toggle');
            const targetElement = document.querySelector(`#${targetId}`);
            const parentElement = collapseTrigger.closest('h3');
            const activateElementSelector = collapseTrigger.getAttribute('data-toggle-activate');
            let activateElement: HTMLElement|null = null;
            if(activateElementSelector){
                activateElement = targetElement?.querySelector(activateElementSelector) as HTMLElement;
            }

            collapseTrigger.addEventListener('click', () => {
                // clearTimeout(timer);

                if (collapseTrigger.getAttribute('aria-expanded') === 'true') {
                    targetElement!.classList.add('hidden');
                    collapseTrigger.setAttribute('aria-expanded', 'false');
                    // if(parentElement){
                    //   timer = setTimeout(() => {
                    parentElement?.classList.remove('active');
                    // }, 250);
                    // }
                } else {
                    targetElement!.classList.remove('hidden');
                    if(activateElement){
                        activateElement.focus();
                    }
                    parentElement?.classList.add('active');
                    collapseTrigger.setAttribute('aria-expanded', 'true');
                }
            });
        });
    }

    setupDropdowns() {
        const dropdowns: NodeListOf<ExtendedElement> = document.querySelectorAll(
            '.dropdown, .filter-list'
        );
        const dropdownMenus: NodeListOf<ExtendedElement> =
            document.querySelectorAll('.dropdown-menu, .filter-menu');

        dropdowns?.forEach((dropdownWrapper) => {
            const trigger = dropdownWrapper.querySelector(
                '.dropdown-trigger, .filter-trigger'
            ) as ExtendedElement;
            const target = dropdownWrapper.querySelector(
                '.dropdown-menu, .filter-menu'
            ) as ExtendedElement;
            target.trigger = trigger;
            trigger.target = target;

            trigger.addEventListener('click', () => {
                // event.preventDefault();
                if (target.classList.contains('hidden')) {
                    // hide all menus
                    this.resetDropdowns(dropdownMenus);
                    this.showDropdown(target);
                } else {
                    this.hideDropdown(target);
                }
            });
        });
    }

    resetDropdowns(dropdownMenus: NodeListOf<ExtendedElement>) {
        dropdownMenus?.forEach((target) => {
            this.hideDropdown(target);
        });
    }

    hideDropdown(target: ExtendedElement) {
        // bail early if already hidden
        if (target.classList.contains('hidden')) {
            return;
        }

        const trigger = target.trigger;
        const { from, to, hideTimeout } = this.animation;
        // clear off click listener
        const animation = target.animate([to, from], hideTimeout);
        trigger.setAttribute('aria-expanded', 'false');
        this._removeListener(document.body, 'click', target.offClickHandler);
        // after animation hide element
        animation.onfinish = () => {
            target.classList.add('hidden');
        };
    }

    showDropdown(target: ExtendedElement) {
        const trigger = target.trigger;
        const { from, to, showTimeout } = this.animation;
        // display selected menu first for animations to work
        target.classList.remove('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        this.dropdownOffClick(target);
        // wait a frame for the display to kick in
        requestAnimationFrame(() => {
            target.animate([from, to], showTimeout);
        });
    }

    dropdownOffClick(target: ExtendedElement) {
        const trigger = target.trigger;
        // add off click listener
        target.offClickHandler = this._addRemovableListener(
            document.body,
            'click',
            (event) => {
                const eventTarget = event.target as HTMLElement;
                if (
                    eventTarget === trigger ||
                    eventTarget === target ||
                    target.contains(eventTarget) ||
                    trigger.contains(eventTarget)
                ) {
                    return;
                }
                this.hideDropdown(target);
            }
        );
    }

    _addRemovableListener(
        element: Element,
        type: string,
        cb: (event: Event) => void
    ): (event: Event) => void {
        const handler = (event: Event): void => {
            cb(event);
        };
        element.addEventListener(type, handler);
        return handler;
    }

    _removeListener(
        element: Element,
        type: string,
        cb: (event: Event) => void
    ): void {
        element.removeEventListener(type, cb);
    }
}